/**
 * Import images from the customer's existing website. See ADR-006.
 *
 * A bulk harvest, not a per-slot swap — the realistic run is "grab the 40 good
 * photographs off their old Wix site before it comes down", which is why this
 * is a multi-select grid on the site detail page rather than a fifth tab in
 * ImageEditModal.
 *
 * Saving goes out in batches because the server caps them: 40 fetches plus 40
 * R2 writes in one request would exceed the gateway timeout. Batching also
 * makes a partial import a coherent state rather than a failed one, and gives
 * the progress bar something real to report.
 */

import { useState, useCallback } from 'react';
import { Card } from './ui/Card';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Spinner } from './ui/Spinner';
import {
  scanSite,
  saveImportedImages,
  IMPORT_BATCH_SIZE,
  type ImportCandidate,
  type ScanSiteResult,
  type SaveOutcome,
} from '../api/media';

interface SiteImageImportProps {
  websiteId: string;
  /** Prefills the URL field when we already know their old address. */
  suggestedUrl?: string | null;
}

export function SiteImageImport({ websiteId, suggestedUrl }: SiteImageImportProps) {
  const [url, setUrl] = useState(suggestedUrl ?? '');
  const [scanning, setScanning] = useState(false);
  const [scan, setScan] = useState<ScanSiteResult | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [outcomes, setOutcomes] = useState<SaveOutcome[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleScan = useCallback(async () => {
    setScanning(true);
    setError(null);
    setOutcomes(null);
    setScan(null);
    setSelected(new Set());

    try {
      const result = await scanSite(websiteId, url);
      setScan(result);
      if (!result.success) {
        setError(result.message ?? 'Could not read that site.');
        return;
      }
      // Preselect everything not already imported — the common case is wanting
      // most of them, and deselecting a few is less work than selecting 40.
      setSelected(
        new Set(result.candidates.filter((c) => !c.alreadyImported).map((c) => c.url))
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Scan failed');
    } finally {
      setScanning(false);
    }
  }, [websiteId, url]);

  const toggle = useCallback((candidateUrl: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(candidateUrl)) next.delete(candidateUrl);
      else next.add(candidateUrl);
      return next;
    });
  }, []);

  const handleSave = useCallback(async () => {
    if (!scan) return;

    const chosen = scan.candidates.filter((c) => selected.has(c.url));
    if (chosen.length === 0) return;

    setSaving(true);
    setError(null);
    setProgress({ done: 0, total: chosen.length });

    const collected: SaveOutcome[] = [];

    try {
      for (let i = 0; i < chosen.length; i += IMPORT_BATCH_SIZE) {
        const batch = chosen.slice(i, i + IMPORT_BATCH_SIZE);
        const result = await saveImportedImages(
          websiteId,
          batch.map(({ url: u, sourcePage, alt, origin }) => ({
            url: u,
            sourcePage,
            alt,
            origin,
          }))
        );
        collected.push(...result.outcomes);
        setProgress({ done: Math.min(i + batch.length, chosen.length), total: chosen.length });
      }
      setOutcomes(collected);
    } catch (err) {
      // Keep whatever succeeded — a partial import is a real result, not a
      // failure to be discarded.
      setOutcomes(collected);
      setError(
        err instanceof Error
          ? `Stopped after ${collected.length} of ${chosen.length}: ${err.message}`
          : 'Save failed partway through'
      );
    } finally {
      setSaving(false);
    }
  }, [scan, selected, websiteId]);

  const savedCount = outcomes?.filter((o) => o.saved).length ?? 0;
  const rejected = outcomes?.filter((o) => !o.saved) ?? [];

  return (
    <Card className="p-6 space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Import from existing site</h3>
        <p className="text-sm text-gray-500">
          Pull photographs off the customer’s current website into their media library —
          before that site comes down.
        </p>
      </div>

      <div className="flex gap-2">
        <Input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://theircurrentsite.com"
          disabled={scanning || saving}
          className="flex-1"
        />
        <Button onClick={handleScan} loading={scanning} disabled={!url.trim() || saving}>
          Scan
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {scanning && (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Spinner /> Reading their site…
        </div>
      )}

      {scan?.success && (
        <div className="space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">
              {scan.candidates.length} images across {scan.pagesVisited.length} pages
              {scan.pagesFailed.length > 0 && (
                <span className="text-amber-600"> · {scan.pagesFailed.length} pages unreadable</span>
              )}
            </span>
            <span className="flex gap-3">
              <button
                onClick={() =>
                  setSelected(
                    new Set(
                      scan.candidates.filter((c) => !c.alreadyImported).map((c) => c.url)
                    )
                  )
                }
                className="text-blue-600 hover:text-blue-800"
              >
                Select all
              </button>
              <button
                onClick={() => setSelected(new Set())}
                className="text-gray-500 hover:text-gray-700"
              >
                Clear
              </button>
            </span>
          </div>

          <p className="text-xs text-gray-500">{scan.note}</p>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 max-h-[28rem] overflow-y-auto">
            {scan.candidates.map((candidate) => (
              <CandidateTile
                key={candidate.url}
                candidate={candidate}
                selected={selected.has(candidate.url)}
                onToggle={() => toggle(candidate.url)}
                disabled={saving}
              />
            ))}
          </div>

          <div className="flex items-center justify-between border-t border-gray-100 pt-3">
            <span className="text-sm text-gray-600">
              {saving
                ? `Importing ${progress.done} of ${progress.total}…`
                : `${selected.size} selected`}
            </span>
            <Button onClick={handleSave} loading={saving} disabled={selected.size === 0}>
              Import {selected.size > 0 ? selected.size : ''} to media library
            </Button>
          </div>

          {saving && progress.total > 0 && (
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-600 transition-all"
                style={{ width: `${(progress.done / progress.total) * 100}%` }}
              />
            </div>
          )}
        </div>
      )}

      {outcomes && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 space-y-2">
          <p className="text-sm font-medium text-green-800">
            {savedCount} image{savedCount === 1 ? '' : 's'} added to the media library.
          </p>
          {rejected.length > 0 && (
            <details className="text-xs text-green-700">
              <summary className="cursor-pointer">
                {rejected.length} skipped — reasons
              </summary>
              <ul className="mt-1 space-y-0.5 font-mono">
                {rejected.map((o) => (
                  <li key={o.url}>
                    {o.url.slice(-44)} — {o.reason}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </Card>
  );
}

function CandidateTile({
  candidate,
  selected,
  onToggle,
  disabled,
}: {
  candidate: ImportCandidate;
  selected: boolean;
  onToggle: () => void;
  disabled: boolean;
}) {
  const unavailable = candidate.alreadyImported;

  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled || unavailable}
      title={candidate.alt || candidate.url}
      className={`relative block rounded-lg overflow-hidden border-2 transition-all text-left ${
        unavailable
          ? 'border-gray-200 opacity-40 cursor-not-allowed'
          : selected
            ? 'border-blue-500 ring-2 ring-blue-200'
            : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      {/* Previewed from the source site. The bytes are only copied to R2 for
          the images the user actually chooses. */}
      <img
        src={candidate.url}
        alt={candidate.alt ?? ''}
        loading="lazy"
        className="w-full h-28 object-cover bg-gray-100"
      />
      {selected && !unavailable && (
        <span className="absolute top-1 right-1 bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
          ✓
        </span>
      )}
      {unavailable && (
        <span className="absolute top-1 right-1 bg-gray-700 text-white rounded px-1.5 py-0.5 text-[10px]">
          imported
        </span>
      )}
      {candidate.alt && (
        <span className="block px-1.5 py-1 text-[10px] text-gray-600 truncate">
          {candidate.alt}
        </span>
      )}
    </button>
  );
}
